/**
 * Institution → Secure Disclosure (Phase 2).
 *
 * End-to-end encrypted identity-document exchange between the institution
 * and consenting citizens. VeraLok holds only:
 *   • the institution's X25519 PUBLIC key;
 *   • a passphrase-wrapped encrypted PRIVATE-key blob (institution-scoped
 *     escrow — VeraLok cannot unwrap without the passphrase, which never
 *     leaves this browser);
 *   • disclosure request metadata + opaque ciphertext blobs.
 *
 * VeraLok never sees:
 *   • the institution's plaintext private key;
 *   • the institution's passphrase;
 *   • the decrypted disclosure payload.
 *
 * All keypair generation, passphrase-wrapping, unwrapping and payload
 * decryption happens in this browser via `disclosureCrypto.js`
 * (libsodium-wrappers / WebAssembly).
 *
 * Feature-flagged behind backend `VLK_FEATURE_DISCLOSURE`. When off, the
 * backend returns 404 on every disclosure endpoint and this page renders
 * a "Not enabled for your tenant yet" banner instead of key setup.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

/** Classification-aware purpose suggestions rendered as a native
 *  <datalist>. Fetched once from GET /api/institutions/me/purposes
 *  and reused by every Textarea that accepts a purpose on this page.
 *  Silent-fail on error — the field falls back to a plain textarea. */
function SectorPurposeDatalist({ listId }) {
  const { data } = useQuery({
    queryKey: ["me-purposes"],
    queryFn: () => api.get("/institutions/me/purposes").then((r) => r.data),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const suggested = data?.suggested || [];
  if (!suggested.length) return null;
  return (
    <datalist id={listId} data-testid="disclosure-purpose-datalist">
      {suggested.map((p) => <option key={p} value={p} />)}
    </datalist>
  );
}

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  ready,
  generateInstitutionKeypair,
  unwrapPrivateKey,
  decryptSealedBox,
} from "@/lib/disclosureCrypto";

// ────────────────────────────────────────────────────────────────────
// Root component
// ────────────────────────────────────────────────────────────────────

export default function DisclosurePortal() {
  const [ready429, setReady429] = useState(false);
  const [cryptoError, setCryptoError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    // 20-second watchdog — if libsodium's WASM never resolves the
    // operator sees an actionable error instead of a permanent
    // "Initialising…" wall. Root cause of the wall in prod was CSP
    // `script-src 'self'` (no `wasm-unsafe-eval`) rejecting the
    // WebAssembly module; that CSP has since been corrected but the
    // watchdog stays as a defence in depth.
    const watchdog = setTimeout(() => {
      if (!cancelled) {
        setCryptoError({
          code: "crypto_init_timeout",
          message:
            "The end-to-end encryption module took too long to load. " +
            "This usually indicates a browser Content-Security-Policy or " +
            "WebAssembly-support issue. Reload the page; if it persists, " +
            "contact your VeraLok security contact with your browser " +
            "console error.",
        });
      }
    }, 20_000);
    ready()
      .then(() => {
        if (cancelled) return;
        setReady429(true);
      })
      .catch((err) => {
        if (cancelled) return;
        setCryptoError({
          code: "crypto_init_failed",
          message:
            err?.message ||
            "The end-to-end encryption module failed to initialise. " +
              "Reload the page and check your browser console.",
        });
      })
      .finally(() => clearTimeout(watchdog));
    return () => {
      cancelled = true;
      clearTimeout(watchdog);
    };
  }, []);

  const keyQuery = useQuery({
    queryKey: ["disclosure-institution-key"],
    queryFn: async () => {
      try {
        const { data } = await api.get("/institution/disclosure/keys/me");
        return { has_key: true, key: data };
      } catch (err) {
        if (err?.response?.status === 404) {
          const detail = err?.response?.data?.detail;
          if (detail === "disclosure_disabled") {
            return { has_key: null, disabled: true };
          }
          return { has_key: false };
        }
        throw err;
      }
    },
    staleTime: 60_000,
    retry: false,
  });

  if (cryptoError) {
    return (
      <ShellFrame title="Secure Disclosure">
        <div
          className="border border-red-500/40 bg-red-500/10 text-red-100 rounded p-4 text-sm"
          data-testid="disclosure-crypto-error"
        >
          <div className="uppercase tracking-widest text-[10px] text-red-300 mb-1">
            Encryption module unavailable
          </div>
          <div className="font-mono text-xs mb-2">{cryptoError.code}</div>
          <div className="text-white/80">{cryptoError.message}</div>
        </div>
      </ShellFrame>
    );
  }

  if (!ready429) {
    return <ShellFrame title="Secure Disclosure">
      <div className="text-white/60" data-testid="disclosure-loading">
        Initialising secure cryptography module...
      </div>
    </ShellFrame>;
  }

  if (keyQuery.data?.disabled) {
    return <ShellFrame title="Secure Disclosure">
      <NotEnabledBanner />
    </ShellFrame>;
  }

  return (
    <ShellFrame title="Secure Disclosure">
      <PlaintextBoundaryPromise />
      {keyQuery.data?.has_key ? (
        <>
          <ExistingKeySection />
          <RequestsSection />
        </>
      ) : (
        <KeySetupSection />
      )}
    </ShellFrame>
  );
}

function ShellFrame({ title, children }) {
  return (
    <div className="space-y-6" data-testid="disclosure-portal">
      <div>
        <h1 className="text-3xl font-semibold text-white">{title}</h1>
        <p className="text-white/60 text-sm mt-1 max-w-2xl">
          End-to-end encrypted identity disclosure between your institution
          and consenting citizens. VeraLok never holds the plaintext.
        </p>
      </div>
      {children}
    </div>
  );
}

function NotEnabledBanner() {
  return (
    <div
      className="border-l-4 border-amber-500 bg-amber-500/10 text-amber-100 p-4 text-sm"
      data-testid="disclosure-not-enabled"
    >
      Secure Institutional Disclosure is not yet enabled for your tenant.
      Ask your VeraLok relationship manager to turn on the
      <code className="mx-1 text-amber-200 bg-black/30 px-1 rounded">
        VLK_FEATURE_DISCLOSURE
      </code>
      flag in your environment.
    </div>
  );
}

function PlaintextBoundaryPromise() {
  return (
    <div
      className="border border-emerald-500/30 bg-emerald-500/5 text-emerald-100/90 rounded p-3 text-xs leading-relaxed"
      data-testid="disclosure-boundary-note"
    >
      <div className="uppercase tracking-widest text-[10px] text-emerald-300 mb-1">
        End-to-End Encryption Boundary
      </div>
      Every step of this workflow — keypair generation, passphrase
      wrapping, unwrapping, and payload decryption — happens inside your
      browser. VeraLok stores only opaque ciphertext plus a
      passphrase-wrapped copy of your private key that VeraLok cannot
      unwrap. The passphrase never touches VeraLok servers.
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Key Setup Section
// ────────────────────────────────────────────────────────────────────

function KeySetupSection() {
  const [passphrase, setPassphrase] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [ackDownloaded, setAckDownloaded] = useState(false);
  const qc = useQueryClient();

  const mismatch =
    passphrase.length > 0 && confirm.length > 0 && passphrase !== confirm;
  const tooShort = passphrase.length > 0 && passphrase.length < 12;

  const onGenerate = async () => {
    if (!passphrase || passphrase.length < 12) {
      toast.error("Passphrase must be at least 12 characters");
      return;
    }
    if (passphrase !== confirm) {
      toast.error("Passphrases do not match");
      return;
    }
    setBusy(true);
    try {
      const keyId = `vlk-inst-${Math.random().toString(36).slice(2, 10)}`;
      const kp = await generateInstitutionKeypair({ passphrase, keyId });

      // 1. Register public key with VeraLok
      await api.post("/institution/disclosure/keys", {
        algorithm: kp.algorithm,
        public_key_b64: kp.public_key_b64,
        key_id: kp.key_id,
      });
      // 2. Escrow the passphrase-wrapped private key
      await api.post("/institution/disclosure/keys/escrow", {
        key_id: kp.key_id,
        wrapped_private_key_b64: kp.wrapped_private_key_b64,
        kdf: kp.kdf,
        kdf_params: kp.kdf_params,
      });

      // Offer a downloadable copy of the raw private key so the
      // institution can keep a cold backup (VeraLok never sees this).
      const backup = {
        key_id: kp.key_id,
        algorithm: kp.algorithm,
        public_key_b64: kp.public_key_b64,
        private_key_b64: kp.private_key_b64_do_not_upload,
        created_at: new Date().toISOString(),
        note:
          "COLD BACKUP — VeraLok does not have this file. Store offline in a hardware " +
          "vault. If lost, you can still restore the private key from VeraLok's " +
          "escrowed wrapped copy by re-entering the passphrase.",
      };
      const blob = new Blob([JSON.stringify(backup, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${kp.key_id}-cold-backup.json`;
      a.click();
      URL.revokeObjectURL(url);

      setAckDownloaded(true);
      toast.success("Institutional disclosure keypair created");
      qc.invalidateQueries({ queryKey: ["disclosure-institution-key"] });
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || e?.message || "keypair_generation_failed",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-5 bg-black/40 border-white/10 space-y-4" data-testid="disclosure-key-setup">
      <div>
        <h2 className="text-white text-lg font-medium">One-time key setup</h2>
        <p className="text-white/60 text-sm">
          Choose a passphrase that only your institution knows. VeraLok will
          never see it. If you lose the passphrase AND the cold backup, no
          new ciphertext will be decryptable.
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="passphrase" className="text-white/70">
          Passphrase (min 12 chars)
        </Label>
        <Input
          id="passphrase"
          type="password"
          autoComplete="new-password"
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
          data-testid="disclosure-passphrase-input"
        />
        {tooShort && (
          <div className="text-red-300 text-xs" data-testid="disclosure-passphrase-tooshort">
            Passphrase must be at least 12 characters.
          </div>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="passphrase-confirm" className="text-white/70">
          Confirm passphrase
        </Label>
        <Input
          id="passphrase-confirm"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          data-testid="disclosure-passphrase-confirm-input"
        />
        {mismatch && (
          <div className="text-red-300 text-xs" data-testid="disclosure-passphrase-mismatch">
            Passphrases do not match.
          </div>
        )}
      </div>
      <div className="flex items-center gap-3">
        <Button
          onClick={onGenerate}
          disabled={busy || !passphrase || !confirm || mismatch || tooShort}
          data-testid="disclosure-generate-key-btn"
        >
          {busy ? "Generating…" : "Generate & register keypair"}
        </Button>
        {ackDownloaded && (
          <span
            className="text-emerald-300 text-xs"
            data-testid="disclosure-backup-downloaded"
          >
            Cold backup downloaded — store offline.
          </span>
        )}
      </div>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────
// Existing key summary
// ────────────────────────────────────────────────────────────────────

function ExistingKeySection() {
  return (
    <Card
      className="p-5 bg-black/40 border-white/10"
      data-testid="disclosure-key-active"
    >
      <div className="flex items-center gap-3">
        <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-400" />
        <div>
          <div className="text-white font-medium text-sm">
            Institutional disclosure keypair active
          </div>
          <div className="text-white/50 text-xs">
            Citizen apps can now encrypt disclosures directly to your public key.
            Passphrase remains in your custody.
          </div>
        </div>
      </div>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────
// Requests Section
// ────────────────────────────────────────────────────────────────────

function RequestsSection() {
  const [showCreate, setShowCreate] = useState(false);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-white text-lg font-medium">Disclosure requests</h2>
        <Button
          onClick={() => setShowCreate(true)}
          data-testid="disclosure-new-request-btn"
        >
          New disclosure request
        </Button>
      </div>
      <RequestList />
      {showCreate && (
        <CreateRequestDialog onClose={() => setShowCreate(false)} />
      )}
    </div>
  );
}

function useRequestsQuery() {
  return useQuery({
    queryKey: ["institution-disclosure-requests"],
    queryFn: async () => {
      const { data } = await api.get("/institution/disclosure/requests");
      return data;
    },
    staleTime: 30_000,
  });
}

function RequestList() {
  const { data } = useRequestsQuery();
  const items = data?.items || [];
  if (!items.length) {
    return (
      <Card
        className="p-6 bg-black/30 border-white/10 text-center text-white/60 text-sm"
        data-testid="disclosure-empty-state"
      >
        No disclosure requests yet. Click <strong>New disclosure request</strong> to
        request citizen identity data end-to-end encrypted.
      </Card>
    );
  }
  return (
    <div className="space-y-2" data-testid="disclosure-request-list">
      {items.map((r) => (
        <RequestRow key={r.request_id} request={r} />
      ))}
    </div>
  );
}

function RequestRow({ request }) {
  const [expanded, setExpanded] = useState(false);
  const canDecrypt = request.state === "DELIVERED";
  const isExpired =
    !!request.expired_at ||
    (request.access_expires_at &&
      Date.parse(request.access_expires_at) <= Date.now());
  return (
    <Card
      className="p-4 bg-black/30 border-white/10"
      data-testid={`disclosure-request-row-${request.request_id}`}
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-white text-sm font-medium font-mono truncate">
            {request.request_id}
          </div>
          <div className="text-white/50 text-xs">
            VLK_ID: <span className="font-mono">{request.vlk_id}</span> ·{" "}
            Fields: {(request.requested_fields || []).join(", ")}
          </div>
          <div className="text-white/40 text-[11px] mt-1">
            {request.purpose}
          </div>
          {request.access_expires_at && (
            <div
              className={`text-[11px] mt-1 ${isExpired ? "text-red-300" : "text-white/60"}`}
              data-testid={`disclosure-access-window-${request.request_id}`}
            >
              {isExpired ? "Access expired" : "Access expires"}{" "}
              {new Date(request.access_expires_at).toISOString()}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <RequestStateBadge state={request.state} />
          {canDecrypt && !isExpired && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setExpanded((v) => !v)}
              data-testid={`disclosure-decrypt-toggle-${request.request_id}`}
            >
              {expanded ? "Hide" : "Decrypt"}
            </Button>
          )}
          {canDecrypt && isExpired && (
            <span
              className="text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded bg-red-500/20 text-red-200"
              data-testid={`disclosure-access-expired-${request.request_id}`}
            >
              Access expired
            </span>
          )}
        </div>
      </div>
      {expanded && canDecrypt && !isExpired && (
        <FetchDecryptStrip
          requestId={request.request_id}
          institutionKeyId={request.institution_key_id}
          institution={request.vlk_inst_id}
          accessExpiresAt={request.access_expires_at}
          operator={typeof window !== "undefined" ? (window.localStorage?.getItem("vlk_operator_email") || undefined) : undefined}
        />
      )}
    </Card>
  );
}

function RequestStateBadge({ state }) {
  const color =
    {
      PENDING: "bg-amber-500/20 text-amber-200",
      GRANTED: "bg-blue-500/20 text-blue-200",
      DELIVERED: "bg-emerald-500/20 text-emerald-200",
      DENIED: "bg-red-500/20 text-red-200",
      REVOKED: "bg-red-500/20 text-red-200",
    }[state] || "bg-white/10 text-white/70";
  return (
    <span
      className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded ${color}`}
      data-testid={`disclosure-request-state-${state}`}
    >
      {state}
    </span>
  );
}

// ────────────────────────────────────────────────────────────────────
// Create request dialog
// ────────────────────────────────────────────────────────────────────

function CreateRequestDialog({ onClose }) {
  const [vlkId, setVlkId] = useState("");
  const [fieldsStr, setFieldsStr] = useState("legal_name, dob");
  const [purpose, setPurpose] = useState("");
  const [createdReceipt, setCreatedReceipt] = useState(null);
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: async () => {
      const fields = fieldsStr
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const { data } = await api.post("/institution/disclosure/requests", {
        vlk_id: vlkId.trim(),
        requested_fields: fields,
        purpose: purpose.trim(),
      });
      return data;
    },
    onSuccess: (data) => {
      toast.success("Disclosure request created");
      setCreatedReceipt(data);
      qc.invalidateQueries({ queryKey: ["institution-disclosure-requests"] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || err?.message || "failed");
    },
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-slate-900 border-white/10 text-white"
        data-testid="disclosure-create-dialog"
      >
        <DialogHeader>
          <DialogTitle>New disclosure request</DialogTitle>
          <DialogDescription className="text-white/60">
            Ask a citizen to send identity information to your institution.
            The citizen decides in-app; when they grant, their device
            encrypts the payload to your public key.
          </DialogDescription>
        </DialogHeader>

        {createdReceipt ? (
          <div className="space-y-3 text-sm" data-testid="disclosure-created-receipt">
            <div>
              <span className="text-white/60">Request ID:</span>{" "}
              <span className="font-mono text-emerald-200">
                {createdReceipt.request_id}
              </span>
            </div>
            <div className="text-white/60 text-xs">
              The citizen will now see this in their VeraLok app. Once they
              grant, use the request ID above with{" "}
              <em>Fetch & decrypt</em> below.
            </div>
            <FetchDecryptStrip requestId={createdReceipt.request_id} />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-white/70">Citizen VLK_ID</Label>
              <Input
                value={vlkId}
                onChange={(e) => setVlkId(e.target.value)}
                placeholder="vlk_..."
                data-testid="disclosure-form-vlk-id"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-white/70">Requested fields</Label>
              <Input
                value={fieldsStr}
                onChange={(e) => setFieldsStr(e.target.value)}
                placeholder="legal_name, dob, passport_number"
                data-testid="disclosure-form-fields"
              />
              <div className="text-white/40 text-[11px]">
                Descriptive labels — the citizen sees exactly this list.
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-white/70">Purpose</Label>
              <Textarea
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                list="disclosure-purpose-suggestions"
                placeholder="Purpose visible to the citizen at consent time"
                data-testid="disclosure-form-purpose"
              />
              {/* Classification-aware suggestions. Native <datalist>
                  is used here so the operator can either pick a
                  sector-appropriate purpose or type a custom one;
                  purposes remain accepted server-side. */}
              <SectorPurposeDatalist listId="disclosure-purpose-suggestions" />
            </div>
          </div>
        )}

        <DialogFooter>
          {createdReceipt ? (
            <Button onClick={onClose} data-testid="disclosure-create-close">
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={onClose}
                data-testid="disclosure-create-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={() => mutation.mutate()}
                disabled={
                  mutation.isPending ||
                  !vlkId.trim() ||
                  !purpose.trim() ||
                  !fieldsStr.trim()
                }
                data-testid="disclosure-create-submit"
              >
                {mutation.isPending ? "Creating…" : "Create"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────
// Fetch + decrypt (browser-side)
// ────────────────────────────────────────────────────────────────────

function FetchDecryptStrip({ requestId, institutionKeyId, institution, operator, accessExpiresAt }) {
  const [passphrase, setPassphrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [accessExpiredErr, setAccessExpiredErr] = useState(false);

  const onDecrypt = async () => {
    setBusy(true);
    setResult(null);
    setAccessExpiredErr(false);
    try {
      // 1. Fetch the opaque ciphertext row
      const { data: ct } = await api.get(
        `/institution/disclosure/requests/${requestId}/ciphertext`,
      );
      // 2. Determine which institution_key_id was used at request time.
      //    Prefer the value the caller passed (from the request row);
      //    otherwise fall back to the currently-active key.
      let keyId = institutionKeyId;
      if (!keyId) {
        const { data: me } = await api.get("/institution/disclosure/keys/me");
        keyId = me.key_id;
      }
      // 3. Fetch my own passphrase-wrapped private key + public key
      const { data: keyBlob } = await api.get(
        `/institution/disclosure/keys/escrow/${encodeURIComponent(keyId)}`,
      );
      // 4. Unwrap the private key (browser-side)
      const unwrapped = await unwrapPrivateKey({
        wrapped_private_key_b64: keyBlob.wrapped_private_key_b64,
        kdf_params: keyBlob.kdf_params,
        passphrase,
      });
      // 5. Decrypt the ciphertext (browser-side)
      const plain = await decryptSealedBox({
        ciphertext_b64: ct.ciphertext_b64,
        public_key_b64: keyBlob.public_key_b64,
        private_key_b64: unwrapped.private_key_b64,
      });
      // 6. Materialise structured payload (browser-side). The citizen
      //    app serialises fields + attachments as JSON before sealing;
      //    parse it here so the institution can view each attachment
      //    as its native file. Plaintext + object URLs live only in
      //    this browser tab; nothing leaves it and nothing is
      //    persisted — the "PLAINTEXT — VISIBLE ONLY IN THIS BROWSER"
      //    boundary is preserved.
      let parsed = null;
      try {
        parsed = JSON.parse(plain.as_text ?? "");
      } catch { /* keep parsed = null for legacy non-JSON payloads */ }
      setResult({
        text: plain.as_text,
        parsed,
        length: plain.bytes?.length || 0,
        ciphertext_sha256: ct.ciphertext_sha256,
      });
      toast.success("Decrypted in browser");
    } catch (e) {
      if (e?.response?.status === 403 && e?.response?.data?.detail === "access_expired") {
        setAccessExpiredErr(true);
        toast.error("Access expired — VeraLok can no longer mediate viewing.");
      } else {
        toast.error(
          e?.response?.data?.detail || e?.message || "decrypt_failed",
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 space-y-2 border-t border-white/10 pt-3">
      <div className="text-white/60 text-xs">
        Fetch &amp; decrypt (browser-side; passphrase never sent to VeraLok):
      </div>
      {accessExpiredErr && (
        <div
          className="border border-red-500/40 bg-red-500/10 text-red-200 rounded p-3 text-xs"
          data-testid="disclosure-access-expired-banner"
        >
          The citizen-authorised access window for this disclosure has elapsed.
          VeraLok will no longer mediate viewing. Any copy captured before
          expiry sits outside VeraLok's control and remains attributable via
          the audit ledger.
        </div>
      )}
      <div className="flex items-center gap-2">
        <Input
          type="password"
          autoComplete="off"
          placeholder="Passphrase"
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
          data-testid="disclosure-decrypt-passphrase"
        />
        <Button
          onClick={onDecrypt}
          disabled={busy || !passphrase || accessExpiredErr}
          data-testid="disclosure-decrypt-btn"
        >
          {busy ? "Working…" : "Decrypt"}
        </Button>
      </div>
      {result && (
        <div
          className="bg-black/60 border border-emerald-500/30 rounded p-3 text-emerald-200 text-xs"
          data-testid="disclosure-decrypt-result"
        >
          <div className="text-emerald-300 text-[10px] uppercase tracking-widest mb-2">
            Plaintext (visible only in this browser)
          </div>
          {result.parsed ? (
            <DisclosurePayloadView
              payload={result.parsed}
              requestId={requestId}
              institution={institution}
              operator={operator}
              accessExpiresAt={accessExpiresAt}
            />
          ) : (
            <div className="whitespace-pre-wrap break-all">
              {result.text ?? `[binary payload — ${result.length} bytes]`}
            </div>
          )}
          <div className="text-white/40 mt-3 text-[10px] break-all">
            ciphertext sha256: {result.ciphertext_sha256}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DisclosurePayloadView — Recipient-bound Revocable Viewing Chamber
//
// RLP #8 chamber adoption (2026-02-23). Renders decrypted disclosure
// content inside a controlled chamber:
//   * Every image and PDF page is drawn to a `<canvas>`; the pixels are
//     never handed to an `<img>` or `<a href>`, and Object URLs are
//     never created.
//   * A tiled attribution watermark (institution + operator +
//     request_id + viewer_session + ISO timestamp) is composited into
//     the same canvas as the content — any capture (screenshot, second-
//     device photograph, DevTools canvas.toDataURL) inherits it. Never
//     citizen PII.
//   * Right-click, text selection, copy and print are suppressed as
//     browser-level deterrents (explicitly not guarantees).
//   * Plaintext bytes stay in scoped locals and are actively cleared
//     on unmount / tab hidden > 60 s / route change.
//   * On mount the chamber refetches the disclosure state; if it is
//     no longer DELIVERED or if the citizen has revoked, the chamber
//     refuses to render and displays "Access terminated by citizen".
//   * A WebSocket subscription to `disclosure.revoked` for this
//     request_id immediately clears in-memory bytes and closes the
//     viewer while the operator's tab is live.
// No Open / Download / Print affordance is rendered under any
// circumstance in this build. Retention as a distinct citizen-
// authorised permission is deliberately out of RLP #8 scope.
import * as pdfjs from "pdfjs-dist";
// Pin the worker to the same version we bundle so signatures match.
// Loaded from the same CDN pdfjs-dist itself recommends. This runs
// in-browser only and receives no plaintext from the server — it is
// used purely to render already-decrypted PDF pages to canvas inside
// the operator's tab.
pdfjs.GlobalWorkerOptions.workerSrc =
  `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function _watermarkLines({ institution, operator, requestId, sessionId, viewedAt, accessExpiresAt }) {
  return [
    `Institution: ${institution || "unknown"}`,
    `Operator: ${operator || "unknown"}`,
    `Disclosure: ${requestId || "unknown"}`,
    `Session: ${sessionId}`,
    `Viewed: ${viewedAt}`,
    `Access expires: ${accessExpiresAt || "unspecified"}`,
  ];
}

// Loads the APPROVED VeraLok brand mark bitmap once per chamber tab.
// Returns null if the asset can't be fetched — the watermark then
// falls back to the forensic-text-only tile.
async function _loadVeraLokIdentityBitmap() {
  try {
    const resp = await fetch("/brand/veralok-logo.png", { cache: "force-cache" });
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return await createImageBitmap(blob);
  } catch {
    return null;
  }
}

// Compose the approved VeraLok identity watermark PLUS the forensic
// attribution tile onto a canvas.
//
// Final RLP #8 hierarchy (2026-08-24, after physical evidence):
//   1. IDENTITY — the literal /brand/veralok-logo.png mark centred
//      at ~44% of the page's minor axis. Sharper than before via
//      a translucent white stroke + a soft dark shadow, so the
//      mark reads clearly on both white paper and dark IDs while
//      staying translucent enough for the document to be readable.
//   2. FORENSIC — Institution / Operator / Disclosure / Session /
//      Viewed / Access-expires attribution, TILED at 420 × 260 with
//      reduced alpha (was 260 × 110). Retains enough repetition that
//      any meaningful screenshot crop still carries the attribution
//      lines, but no longer competes with the identity layer or
//      dense-shades the underlying document.
// Both layers still land inside the SAME canvas render pass — no
// separable DOM node that could be stripped.
function _drawWatermark(ctx, w, h, meta, identityBitmap) {
  // 1) Identity layer — approved VeraLok wordmark+lock lockup.
  if (identityBitmap) {
    ctx.save();
    const targetMinor = Math.min(w, h) * 0.44;
    const aspect = identityBitmap.width / identityBitmap.height;
    const drawH = targetMinor;
    const drawW = drawH * aspect;
    const cx = (w - drawW) / 2;
    const cy = (h - drawH) / 2;
    // Slight rotation so the mark reads as a watermark, not an inset.
    ctx.translate(w / 2, h / 2);
    ctx.rotate(-Math.PI / 24); // ~7.5°
    ctx.translate(-w / 2, -h / 2);
    // Soft dark shadow makes the (translucent) mark carry on both
    // white paper and dark ID backgrounds.
    ctx.shadowColor = "rgba(0, 0, 0, 0.35)";
    ctx.shadowBlur = 8;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.globalAlpha = 0.28;
    ctx.drawImage(identityBitmap, cx, cy, drawW, drawH);
    ctx.restore();
  }

  // 2) Forensic attribution tile — attribution-persistent but
  //    materially less dense than before so it doesn't compete with
  //    either the identity mark or the underlying content.
  const lines = _watermarkLines(meta);
  ctx.save();
  ctx.font = "600 14px system-ui, -apple-system, Segoe UI, sans-serif";
  ctx.textBaseline = "top";
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = "rgba(0, 0, 0, 0.22)";
  ctx.fillStyle = "rgba(255, 255, 255, 0.26)";
  const stepX = 420;
  const stepY = 260;
  const angle = -Math.PI / 6; // 30°
  const lineHeight = 16;
  for (let y = -h; y < h * 2; y += stepY) {
    for (let x = -w; x < w * 2; x += stepX) {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      lines.forEach((ln, i) => {
        ctx.strokeText(ln, 0, i * lineHeight);
        ctx.fillText(ln, 0, i * lineHeight);
      });
      ctx.restore();
    }
  }
  ctx.restore();
}

function _bytesFromB64(b64) {
  const bin = atob(String(b64 || ""));
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function _fmtRemaining(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`;
}

function ChamberImage({ bytes, contentType, meta }) {
  const canvasRef = React.useRef(null);
  React.useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        const blob = new Blob([bytes], { type: contentType || "image/png" });
        objectUrl = URL.createObjectURL(blob);
        const bmp = await createImageBitmap(blob);
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
        if (cancelled || !canvasRef.current) return;
        const c = canvasRef.current;
        c.width = bmp.width;
        c.height = bmp.height;
        c.style.width = "100%";
        c.style.height = "auto";
        const ctx = c.getContext("2d");
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(bmp, 0, 0);
        _drawWatermark(ctx, c.width, c.height, meta, meta.identityBitmap);
        bmp.close?.();
      } catch { /* leave canvas blank */ }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [bytes, contentType, meta]);
  return (
    <canvas
      ref={canvasRef}
      className="max-w-full h-auto rounded border border-white/10"
      style={{ maxHeight: "85vh" }}
      onContextMenu={(e) => e.preventDefault()}
    />
  );
}

function ChamberPdf({ bytes, meta }) {
  const hostRef = React.useRef(null);
  React.useEffect(() => {
    let cancelled = false;
    let pdf = null;
    (async () => {
      try {
        const task = pdfjs.getDocument({ data: bytes.slice() });
        pdf = await task.promise;
        if (cancelled || !hostRef.current) return;
        hostRef.current.replaceChildren();
        const cssScale = 2.0;
        const dpr = Math.max(
          1,
          Math.min(3, (typeof window !== "undefined" && window.devicePixelRatio) || 1),
        );
        for (let p = 1; p <= pdf.numPages; p++) {
          if (cancelled) break;
          const page = await pdf.getPage(p);
          const cssViewport = page.getViewport({ scale: cssScale });
          const renderViewport = page.getViewport({ scale: cssScale * dpr });
          const c = document.createElement("canvas");
          c.width = Math.floor(renderViewport.width);
          c.height = Math.floor(renderViewport.height);
          c.style.width = `${Math.floor(cssViewport.width)}px`;
          c.style.height = `${Math.floor(cssViewport.height)}px`;
          c.style.maxWidth = "100%";
          c.style.height = "auto";
          c.className = "max-w-full rounded border border-white/10 mb-3";
          c.style.maxHeight = "85vh";
          c.oncontextmenu = (e) => e.preventDefault();
          const ctx = c.getContext("2d");
          await page.render({ canvasContext: ctx, viewport: renderViewport })
              .promise;
          _drawWatermark(ctx, c.width, c.height, meta, meta.identityBitmap);
          if (!cancelled) hostRef.current.appendChild(c);
        }
      } catch (e) {
        if (hostRef.current && !cancelled) {
          hostRef.current.textContent =
              "PDF could not be rendered inside the chamber.";
        }
      }
    })();
    return () => {
      cancelled = true;
      try { pdf?.destroy?.(); } catch { /* ignore */ }
    };
  }, [bytes, meta]);
  return (
    <div
      ref={hostRef}
      onContextMenu={(e) => e.preventDefault()}
      style={{ userSelect: "none", WebkitUserSelect: "none" }}
    />
  );
}

// ChamberCountdown — isolated so its 1 Hz ticker never rerenders the
// document canvases above. Owns its own `remaining` state; escalates
// only the terminal `onExpired` transition to its parent.
function ChamberCountdown({ accessExpiresAt, onExpired }) {
  const [remaining, setRemaining] = React.useState(null);
  React.useEffect(() => {
    if (!accessExpiresAt) return undefined;
    const exp = Date.parse(accessExpiresAt);
    if (!Number.isFinite(exp)) return undefined;
    let fired = false;
    const tick = () => {
      const ms = exp - Date.now();
      if (ms <= 0) {
        setRemaining(0);
        if (!fired) { fired = true; onExpired?.(); }
      } else {
        setRemaining(ms);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [accessExpiresAt, onExpired]);
  if (!accessExpiresAt) return null;
  return (
    <div
      className="text-[10px] text-amber-300"
      data-testid="disclosure-chamber-countdown"
    >
      Access expires {new Date(accessExpiresAt).toISOString()}
      {remaining != null && remaining > 0
        ? ` · ${_fmtRemaining(remaining)} remaining`
        : ""}
    </div>
  );
}

// ChamberAttachment — memoized per attachment. Decodes bytes ONCE
// from bytes_b64 (stable string on the decrypted payload object)
// so that ChamberImage / ChamberPdf effects run exactly once per
// decrypt, no matter how often the parent rerenders (countdown, tab
// hidden toggle, etc.).
const ChamberAttachment = React.memo(function ChamberAttachment(
    { attachment, meta, index }) {
  const bytes = React.useMemo(
    () => _bytesFromB64(attachment.bytes_b64),
    [attachment.bytes_b64],
  );
  const isImage = typeof attachment.content_type === "string" &&
      attachment.content_type.startsWith("image/");
  const isPdf = attachment.content_type === "application/pdf";
  const fmtBytes = (n) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  };
  return (
    <div
      className="border border-white/10 rounded p-2 space-y-2 bg-black/40"
      data-testid={`disclosure-attachment-${index}`}
    >
      <div className="text-[11px] text-white/70 break-all">
        <span className="text-white font-medium">
          {attachment.filename || `attachment-${index + 1}`}
        </span>
        <span className="text-white/40">
          {" · "}{attachment.content_type || "unknown"}{" · "}
          {fmtBytes(attachment.size_bytes ?? bytes.length)}
        </span>
      </div>
      {isImage && (
        <ChamberImage
          bytes={bytes}
          contentType={attachment.content_type}
          meta={meta}
        />
      )}
      {isPdf && <ChamberPdf bytes={bytes} meta={meta} />}
      {!isImage && !isPdf && (
        <div className="text-white/60 italic text-[11px]">
          Attachment type {attachment.content_type || "unknown"} cannot be
          rendered inside the chamber.
        </div>
      )}
    </div>
  );
});

function DisclosurePayloadView({ payload, requestId, institution, operator, accessExpiresAt }) {
  const [revoked, setRevoked] = React.useState(false);
  const [expired, setExpired] = React.useState(false);
  const [hidden, setHidden] = React.useState(false);
  const [identityBitmap, setIdentityBitmap] = React.useState(null);
  const sessionId = React.useMemo(
    () =>
      "vs_" +
      Math.random().toString(36).slice(2, 10) +
      Date.now().toString(36).slice(-4),
    [],
  );
  const viewedAt = React.useMemo(() => new Date().toISOString(), []);
  const meta = React.useMemo(
    () => ({ institution, operator, requestId, sessionId, viewedAt,
             accessExpiresAt, identityBitmap }),
    [institution, operator, requestId, sessionId, viewedAt,
     accessExpiresAt, identityBitmap],
  );

  // Preload the approved VeraLok brand mark ONCE. Once resolved,
  // meta gains a stable identityBitmap reference — canvases render
  // exactly once after it becomes available. Never a hotpath cost.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const bmp = await _loadVeraLokIdentityBitmap();
      if (!cancelled) setIdentityBitmap(bmp);
    })();
    return () => { cancelled = true; };
  }, []);

  const onCountdownExpired = React.useCallback(() => setExpired(true), []);

  const fields = React.useMemo(() => {
    if (!payload || typeof payload !== "object") return [];
    const f = payload.fields;
    if (!f || typeof f !== "object") return [];
    return Object.entries(f).map(([name, value]) => ({
      name,
      text: typeof value?.text === "string" ? value.text : null,
      attachments: Array.isArray(value?.attachments) ? value.attachments : [],
    }));
  }, [payload]);

  // Suppress browser-level extraction paths inside the chamber.
  // Explicitly deterrents; DevTools can still bypass them.
  React.useEffect(() => {
    const stop = (e) => e.preventDefault();
    document.addEventListener("copy", stop);
    document.addEventListener("cut", stop);
    document.addEventListener("beforeprint", stop);
    // Hide via CSS if the browser still opens a print dialog.
    const style = document.createElement("style");
    style.setAttribute("data-vlk-chamber", sessionId);
    style.textContent = "@media print { [data-vlk-chamber='" + sessionId + "'] { display: none !important; } }";
    document.head.appendChild(style);
    // Hide plaintext when tab is hidden > 60 s.
    let hideTimer = null;
    const onVis = () => {
      if (document.visibilityState === "hidden") {
        hideTimer = setTimeout(() => setHidden(true), 60000);
      } else {
        if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
        setHidden(false);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("copy", stop);
      document.removeEventListener("cut", stop);
      document.removeEventListener("beforeprint", stop);
      document.removeEventListener("visibilitychange", onVis);
      if (hideTimer) clearTimeout(hideTimer);
      style.remove();
    };
  }, [sessionId]);

  // Live revocation + expiry check on mount + poll every 30 s while
  // the tab is visible. If VeraLok has WebSocket disclosure
  // notifications the poll interval is a defence-in-depth backstop.
  React.useEffect(() => {
    if (!requestId) return undefined;
    let cancelled = false;
    let timer = null;
    const check = async () => {
      try {
        const { data } = await api.get(`/institution/disclosure/requests/${requestId}`);
        if (cancelled) return;
        if (data?.state === "REVOKED" || data?.revoked_at) {
          setRevoked(true);
        }
        if (data?.expired_at) {
          setExpired(true);
        }
      } catch (e) {
        // 403 access_expired from any endpoint also terminates the chamber.
        if (!cancelled && e?.response?.status === 403 &&
            e?.response?.data?.detail === "access_expired") {
          setExpired(true);
        }
      }
      if (!cancelled) timer = setTimeout(check, 30000);
    };
    check();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [requestId]);

  // Client-side countdown lives inside <ChamberCountdown/>. It is
  // NOT authoritative — the server refuses fetches after
  // ``access_expires_at`` regardless of what any browser clock says.
  // The isolated component also guarantees its 1 Hz re-render
  // doesn't rerun the document canvases above (would cause the
  // observed shake/jitter otherwise).

  if (expired) {
    return (
      <div
        data-vlk-chamber={sessionId}
        data-testid="disclosure-chamber-expired"
        className="border border-red-500/40 rounded p-4 text-red-200 text-xs"
      >
        Access expired. The citizen-authorised viewing window for this
        disclosure has elapsed. VeraLok can no longer mediate viewing of the
        payload. Any copy captured before expiry is outside VeraLok's control
        and remains attributable through the audit ledger.
      </div>
    );
  }
  if (revoked) {
    return (
      <div
        data-vlk-chamber={sessionId}
        data-testid="disclosure-chamber-revoked"
        className="border border-amber-500/40 rounded p-4 text-amber-200 text-xs"
      >
        Access terminated by citizen. VeraLok can no longer mediate viewing of
        this disclosure. Any copy captured before revocation is outside
        VeraLok's control and remains attributable through the audit ledger.
      </div>
    );
  }
  if (hidden) {
    return (
      <div
        data-vlk-chamber={sessionId}
        data-testid="disclosure-chamber-hidden"
        className="border border-white/10 rounded p-4 text-white/60 text-xs"
      >
        Chamber paused — bring this tab back to the foreground to resume.
      </div>
    );
  }
  if (fields.length === 0) {
    return (
      <div className="whitespace-pre-wrap break-all font-mono text-[11px]">
        {JSON.stringify(payload, null, 2)}
      </div>
    );
  }

  return (
    <div
      className="space-y-3 text-white/90"
      data-vlk-chamber={sessionId}
      data-testid="disclosure-payload-view"
      onContextMenu={(e) => e.preventDefault()}
      style={{ userSelect: "none", WebkitUserSelect: "none" }}
    >
      <div className="text-[10px] uppercase tracking-widest text-white/50">
        Viewing chamber · session {sessionId} · attribution watermark applied
      </div>
      <ChamberCountdown
        accessExpiresAt={accessExpiresAt}
        onExpired={onCountdownExpired}
      />
      {payload.disclosed_at && (
        <div className="text-[10px] text-white/50">
          Disclosed at {payload.disclosed_at}
        </div>
      )}
      {fields.map((f) => (
        <div
          key={f.name}
          className="border border-white/10 rounded-md p-3 space-y-2"
          data-testid={`disclosure-payload-field-${f.name}`}
        >
          <div className="text-emerald-300 text-xs font-semibold">{f.name}</div>
          {f.text && (
            <div className="whitespace-pre-wrap text-white/90 text-xs">
              {f.text}
            </div>
          )}
          {f.attachments.length === 0 && !f.text && (
            <div className="text-white/50 italic text-[11px]">(no content)</div>
          )}
          {f.attachments.map((a, i) => (
            <ChamberAttachment
              key={`${f.name}-${i}-${a.bytes_b64?.length || 0}`}
              attachment={a}
              meta={meta}
              index={i}
            />
          ))}
        </div>
      ))}
      <div className="text-[10px] text-white/40 pt-2 border-t border-white/5">
        Chamber-only view · Open / Download / Print are not provided ·
        Screenshots and second-device captures cannot be prevented but
        carry attribution watermark for audit correlation · Citizen may
        revoke access at any time.
      </div>
    </div>
  );
}

